import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ExclamationCircleIcon,
  CurrencyRupeeIcon,
  SearchIcon,
  FilterIcon,
  EyeIcon,
} from "@heroicons/react/outline";
import {
  listCollectionsThunk,
  recordCollectionPaymentThunk,
  waiveCollectionThunk,
  resolveCollectionThunk,
} from "../../../store/thunks/jewelleryPlan/cancellationThunks";
import {
  selectCollections,
  selectCollectionsPagination,
  selectCollectionsLoading,
  selectCollectionError,
  selectCollectionActionLoading,
} from "../../../store/slices/jewelleryPlan/cancellationSlice";

const CollectionManagement = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const collections = useSelector(selectCollections);
  const pagination = useSelector(selectCollectionsPagination);
  const loading = useSelector(selectCollectionsLoading);
  const actionLoading = useSelector(selectCollectionActionLoading);
  const error = useSelector(selectCollectionError);

  const [filters, setFilters] = useState({
    status: "",
    branchId: "",
    page: 1,
    limit: 20,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showWaiveModal, setShowWaiveModal] = useState(false);

  const [paymentData, setPaymentData] = useState({
    amount: "",
    method: "CASH",
    reference: "",
  });

  const [waiveData, setWaiveData] = useState({
    reason: "",
    approvalNote: "",
  });

  useEffect(() => {
    dispatch(listCollectionsThunk(filters));
  }, [dispatch, filters]);

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: {
        bg: "bg-warning-light",
        text: "text-warning-dark",
        icon: ClockIcon,
        label: "Pending",
      },
      NOTIFIED: {
        bg: "bg-secondary-light",
        text: "text-secondary-dark",
        icon: ExclamationCircleIcon,
        label: "Notified",
      },
      PARTIAL_COLLECTED: {
        bg: "bg-primary-light",
        text: "text-primary-dark",
        icon: ClockIcon,
        label: "Partial",
      },
      COLLECTED: {
        bg: "bg-success-light",
        text: "text-success-dark",
        icon: CheckCircleIcon,
        label: "Collected",
      },
      WAIVED: {
        bg: "bg-neutral-light",
        text: "text-neutral-dark",
        icon: CheckCircleIcon,
        label: "Waived",
      },
      WRITTEN_OFF: {
        bg: "bg-danger-light",
        text: "text-danger-dark",
        icon: XCircleIcon,
        label: "Written Off",
      },
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    const Icon = config.icon;

    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
      >
        <Icon className="h-4 w-4 mr-1" />
        {config.label}
      </span>
    );
  };

  const handleRecordPayment = async () => {
    if (!selectedCollection || !paymentData.amount || !paymentData.method) {
      alert("Please fill all required fields");
      return;
    }

    try {
      await dispatch(
        recordCollectionPaymentThunk({
          collectionRequestId: selectedCollection._id,
          paymentData: {
            amount: parseFloat(paymentData.amount),
            method: paymentData.method,
            reference: paymentData.reference,
          },
        })
      ).unwrap();

      setShowPaymentModal(false);
      setSelectedCollection(null);
      setPaymentData({ amount: "", method: "CASH", reference: "" });
      dispatch(listCollectionsThunk(filters));
    } catch (error) {
      alert("Failed to record payment: " + error);
    }
  };

  const handleWaive = async () => {
    if (!selectedCollection || !waiveData.reason) {
      alert("Please provide a reason for waiving");
      return;
    }

    try {
      await dispatch(
        waiveCollectionThunk({
          collectionRequestId: selectedCollection._id,
          waiveData: {
            reason: waiveData.reason,
            approvalNote: waiveData.approvalNote,
          },
        })
      ).unwrap();

      setShowWaiveModal(false);
      setSelectedCollection(null);
      setWaiveData({ reason: "", approvalNote: "" });
      dispatch(listCollectionsThunk(filters));
    } catch (error) {
      alert("Failed to waive collection: " + error);
    }
  };

  const handlePageChange = (newPage) => {
    setFilters({ ...filters, page: newPage });
  };

  const filteredCollections = collections.filter((collection) => {
    if (!searchTerm) return true;
    return (
      collection.planId?.planNumber
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      collection.customerId?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const paymentMethods = [
    { value: "CASH", label: "Cash" },
    { value: "UPI", label: "UPI" },
    { value: "CARD", label: "Card" },
    { value: "BANK_TRANSFER", label: "Bank Transfer" },
    { value: "CHEQUE", label: "Cheque" },
  ];

  if (loading && collections.length === 0) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text">Collection Management</h1>
        <p className="mt-1 text-sm text-neutral-dark">
          Manage outstanding collection requests from cancellations
        </p>
      </div>

      {/* Filters & Search */}
      <div className="bg-background rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-neutral-dark" />
              <input
                type="text"
                placeholder="Search by plan number or customer ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-neutral-light rounded-lg focus:ring-primary focus:border-primary"
              />
            </div>
          </div>
          <select
            value={filters.status}
            onChange={(e) =>
              setFilters({ ...filters, status: e.target.value, page: 1 })
            }
            className="px-4 py-2 border border-neutral-light rounded-lg focus:ring-primary focus:border-primary"
          >
            <option value="">All Status</option>
            <option value="PENDING">Pending</option>
            <option value="NOTIFIED">Notified</option>
            <option value="PARTIAL_COLLECTED">Partial Collected</option>
            <option value="COLLECTED">Collected</option>
            <option value="WAIVED">Waived</option>
            <option value="WRITTEN_OFF">Written Off</option>
          </select>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-warning-light rounded-lg shadow p-4">
          <p className="text-sm text-warning-dark">Pending</p>
          <p className="text-2xl font-bold text-warning-dark">
            {collections.filter((c) => c.status === "PENDING").length}
          </p>
        </div>
        <div className="bg-primary-light rounded-lg shadow p-4">
          <p className="text-sm text-primary-dark">Partial</p>
          <p className="text-2xl font-bold text-primary-dark">
            {collections.filter((c) => c.status === "PARTIAL_COLLECTED").length}
          </p>
        </div>
        <div className="bg-success-light rounded-lg shadow p-4">
          <p className="text-sm text-success-dark">Collected</p>
          <p className="text-2xl font-bold text-success-dark">
            {collections.filter((c) => c.status === "COLLECTED").length}
          </p>
        </div>
        <div className="bg-neutral-light rounded-lg shadow p-4">
          <p className="text-sm text-neutral-dark">Waived</p>
          <p className="text-2xl font-bold text-neutral-dark">
            {collections.filter((c) => c.status === "WAIVED").length}
          </p>
        </div>
        <div className="bg-background rounded-lg shadow p-4">
          <p className="text-sm text-neutral-dark">Total Outstanding</p>
          <p className="text-2xl font-bold text-danger">
            {formatAmount(
              collections
                .filter(
                  (c) => c.status === "PENDING" || c.status === "NOTIFIED"
                )
                .reduce(
                  (sum, c) => sum + (c.amount - (c.collectedAmount || 0)),
                  0
                )
            )}
          </p>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 bg-danger-light border border-danger rounded-lg p-4">
          <div className="flex items-start">
            <XCircleIcon className="h-5 w-5 text-danger mt-0.5 mr-2" />
            <div>
              <p className="text-sm font-medium text-danger-dark">Error</p>
              <p className="text-sm text-danger-dark mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Collections List */}
      <div className="bg-background rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-light">
            <thead className="bg-neutral-light">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                  Plan Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                  Amount Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                  Reason
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-dark uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-background divide-y divide-neutral-light">
              {filteredCollections.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center">
                    <CurrencyRupeeIcon className="h-12 w-12 text-neutral mx-auto mb-2" />
                    <p className="text-neutral-dark">No collections found</p>
                  </td>
                </tr>
              ) : (
                filteredCollections.map((collection) => {
                  const remaining =
                    collection.amount - (collection.collectedAmount || 0);
                  const percentage =
                    ((collection.collectedAmount || 0) / collection.amount) *
                    100;

                  return (
                    <tr
                      key={collection._id}
                      className="hover:bg-neutral-light transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <p className="text-sm font-medium text-text">
                            {collection.planId?.planNumber || "N/A"}
                          </p>
                          <p className="text-xs text-neutral-dark">
                            {collection.branchCode || "N/A"}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <p className="text-sm font-bold text-text">
                            {formatAmount(collection.amount)}
                          </p>
                          {collection.collectedAmount > 0 && (
                            <>
                              <p className="text-xs text-success-dark">
                                Collected:{" "}
                                {formatAmount(collection.collectedAmount)}
                              </p>
                              <p className="text-xs text-warning-dark">
                                Remaining: {formatAmount(remaining)}
                              </p>
                              <div className="w-24 bg-neutral-light rounded-full h-2 mt-1">
                                <div
                                  className="bg-success h-2 rounded-full"
                                  style={{ width: `${percentage}%` }}
                                ></div>
                              </div>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-text max-w-xs truncate">
                          {collection.reason}
                        </p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(collection.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-xs text-neutral-dark">
                          {formatDate(collection.createdAt)}
                        </p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex space-x-2">
                          <button
                            onClick={() =>
                              navigate(
                                `/jewellery/collections/${collection._id}`
                              )
                            }
                            className="text-primary hover:text-primary-dark"
                            title="View Details"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </button>
                          {(collection.status === "PENDING" ||
                            collection.status === "NOTIFIED" ||
                            collection.status === "PARTIAL_COLLECTED") && (
                            <>
                              <button
                                onClick={() => {
                                  setSelectedCollection(collection);
                                  setPaymentData({
                                    amount: remaining.toString(),
                                    method: "CASH",
                                    reference: "",
                                  });
                                  setShowPaymentModal(true);
                                }}
                                className="px-3 py-1 bg-success text-white rounded hover:bg-success-dark text-xs"
                              >
                                Record Payment
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedCollection(collection);
                                  setShowWaiveModal(true);
                                }}
                                className="px-3 py-1 bg-warning text-white rounded hover:bg-warning-dark text-xs"
                              >
                                Waive
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-6 py-4 border-t border-neutral-light">
            <div className="flex items-center justify-between">
              <div className="text-sm text-neutral-dark">
                Showing {(pagination.page - 1) * filters.limit + 1} to{" "}
                {Math.min(pagination.page * filters.limit, pagination.total)} of{" "}
                {pagination.total} results
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="px-3 py-1 border border-neutral-light rounded hover:bg-neutral-light disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className="px-3 py-1 border border-neutral-light rounded hover:bg-neutral-light disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedCollection && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-background rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-text mb-4">
              Record Payment
            </h3>
            <div className="mb-4">
              <p className="text-sm text-neutral-dark">Plan Number</p>
              <p className="font-medium">
                {selectedCollection.planId?.planNumber || "N/A"}
              </p>
            </div>
            <div className="mb-4">
              <p className="text-sm text-neutral-dark">Total Amount Due</p>
              <p className="text-lg font-bold text-text">
                {formatAmount(selectedCollection.amount)}
              </p>
              {selectedCollection.collectedAmount > 0 && (
                <p className="text-sm text-success-dark">
                  Already collected:{" "}
                  {formatAmount(selectedCollection.collectedAmount)}
                </p>
              )}
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-text mb-1">
                Payment Amount *
              </label>
              <input
                type="number"
                value={paymentData.amount}
                onChange={(e) =>
                  setPaymentData({ ...paymentData, amount: e.target.value })
                }
                className="w-full px-3 py-2 border border-neutral-light rounded-lg focus:ring-primary focus:border-primary"
                placeholder="Enter amount..."
                min="0"
                step="0.01"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-text mb-1">
                Payment Method *
              </label>
              <select
                value={paymentData.method}
                onChange={(e) =>
                  setPaymentData({ ...paymentData, method: e.target.value })
                }
                className="w-full px-3 py-2 border border-neutral-light rounded-lg focus:ring-primary focus:border-primary"
              >
                {paymentMethods.map((method) => (
                  <option key={method.value} value={method.value}>
                    {method.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-text mb-1">
                Reference (Optional)
              </label>
              <input
                type="text"
                value={paymentData.reference}
                onChange={(e) =>
                  setPaymentData({ ...paymentData, reference: e.target.value })
                }
                className="w-full px-3 py-2 border border-neutral-light rounded-lg focus:ring-primary focus:border-primary"
                placeholder="Transaction reference..."
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setSelectedCollection(null);
                  setPaymentData({ amount: "", method: "CASH", reference: "" });
                }}
                disabled={actionLoading}
                className="px-4 py-2 border border-neutral-light rounded-lg hover:bg-neutral-light disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRecordPayment}
                disabled={actionLoading || !paymentData.amount}
                className="px-4 py-2 bg-success text-white rounded-lg hover:bg-success-dark disabled:opacity-50"
              >
                {actionLoading ? "Recording..." : "Record Payment"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Waive Modal */}
      {showWaiveModal && selectedCollection && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-background rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-text mb-4">
              Waive Collection
            </h3>
            <div className="mb-4 p-3 bg-warning-light rounded-lg">
              <p className="text-sm text-warning-dark">
                ⚠️ This action will write off the outstanding amount. This
                cannot be undone.
              </p>
            </div>
            <div className="mb-4">
              <p className="text-sm text-neutral-dark">Plan Number</p>
              <p className="font-medium">
                {selectedCollection.planId?.planNumber || "N/A"}
              </p>
            </div>
            <div className="mb-4">
              <p className="text-sm text-neutral-dark">Amount to Waive</p>
              <p className="text-lg font-bold text-danger">
                {formatAmount(
                  selectedCollection.amount -
                    (selectedCollection.collectedAmount || 0)
                )}
              </p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-text mb-1">
                Reason for Waiving *
              </label>
              <textarea
                value={waiveData.reason}
                onChange={(e) =>
                  setWaiveData({ ...waiveData, reason: e.target.value })
                }
                rows={3}
                className="w-full px-3 py-2 border border-neutral-light rounded-lg focus:ring-primary focus:border-primary"
                placeholder="Explain why this amount is being waived..."
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-text mb-1">
                Approval Note (Optional)
              </label>
              <textarea
                value={waiveData.approvalNote}
                onChange={(e) =>
                  setWaiveData({ ...waiveData, approvalNote: e.target.value })
                }
                rows={2}
                className="w-full px-3 py-2 border border-neutral-light rounded-lg focus:ring-primary focus:border-primary"
                placeholder="Add approval notes..."
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowWaiveModal(false);
                  setSelectedCollection(null);
                  setWaiveData({ reason: "", approvalNote: "" });
                }}
                disabled={actionLoading}
                className="px-4 py-2 border border-neutral-light rounded-lg hover:bg-neutral-light disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleWaive}
                disabled={actionLoading || !waiveData.reason}
                className="px-4 py-2 bg-danger text-white rounded-lg hover:bg-danger-dark disabled:opacity-50"
              >
                {actionLoading ? "Waiving..." : "Waive Collection"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollectionManagement;
